import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { api, User } from '../lib/api';
import { motion } from 'framer-motion';

export const Landing: React.FC = () => {
  const { connected } = useWallet();
  const { setVisible } = useWalletModal();
  const navigate = useNavigate();
  
  const [creators, setCreators] = useState<(User & { follower_count: number })[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [copied, setCopied] = useState<boolean>(false);

  // Mint address placeholder
  const MINT_ADDRESS = 'PLACEHOLDER_PUMP_FUN_MINT_ADDRESS';

  useEffect(() => {
    const fetchTopCreators = async () => {
      try {
        const res = await api.getCreators(1, 3);
        setCreators(res.creators);
      } catch (err) {
        console.error('Failed to load creators for landing page', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTopCreators();
  }, []);

  const handleHeroCTA = () => {
    if (connected) {
      navigate('/explore');
    } else {
      setVisible(true);
    }
  };

  const handleCopyMint = () => {
    navigator.clipboard.writeText(MINT_ADDRESS);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const mockCreators = [
    { name: 'Princess Arch', bio: 'Diamond sole content creator. Top 0.01% feet.', followers: '2.5K', avatar: '👣' },
    { name: 'Goldie Toes', bio: 'Soft heels and high arches. Daily uploads.', followers: '1.8K', avatar: '🦶' },
    { name: 'Satin Soles', bio: 'Ticklish feet, barefoot walks, oil plays.', followers: '1.2K', avatar: '👣' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5rem', paddingBottom: '4rem' }}>
      
      {/* 1. HERO SECTION */}
      <section style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        textAlign: 'center', 
        padding: '4rem 0 2rem 0',
        position: 'relative'
      }}>
        {/* Floating Background Icons */}
        <motion.div 
          style={{ position: 'absolute', top: '5%', left: '10%', fontSize: '2.5rem', zIndex: -1, opacity: 0.25 }}
          animate={{ y: [0, -12, 0], rotate: [0, 8, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          🦶
        </motion.div>
        <motion.div 
          style={{ position: 'absolute', top: '35%', right: '10%', fontSize: '2rem', zIndex: -1, opacity: 0.25 }}
          animate={{ y: [0, 12, 0], rotate: [0, -10, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          👣
        </motion.div>

        {/* Token/Coin Brand Image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7 }}
          style={{ marginBottom: '1.5rem' }}
        >
          <img 
            src="/logo.png" 
            alt="SOLEMATE Token Logo" 
            style={{ 
              width: '120px', 
              height: '120px', 
              borderRadius: '50%', 
              border: '2px solid rgba(255, 45, 120, 0.4)',
              boxShadow: '0 0 40px rgba(255, 45, 120, 0.35)',
            }} 
          />
        </motion.div>

        <motion.h1 
          className="gradient-text text-glow"
          style={{ fontSize: '4.25rem', lineHeight: 1.05, marginBottom: '1.25rem', fontWeight: 900 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          SOLEMATE
        </motion.h1>

        <motion.p 
          style={{ fontSize: '1.35rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '2.5rem', maxWidth: '650px', lineHeight: 1.4 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          The Premium Decentralized Lounge for Exclusive Foot Aesthetics & Art 👣✨
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}
        >
          <button className="btn btn-primary" style={{ padding: '1.1rem 2.8rem', fontSize: '1.1rem' }} onClick={handleHeroCTA}>
            {connected ? 'Enter Aesthetics Feed 👣' : 'Connect Wallet to Enter 🔑'}
          </button>
          
          {/* Mint Address HUD */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '0.5rem', 
            background: 'rgba(255,255,255,0.02)', 
            border: '1px solid var(--border-glass)',
            padding: '0.5rem 1rem', 
            borderRadius: '9999px',
            fontSize: '0.8rem',
            color: 'var(--text-secondary)',
            marginTop: '1rem'
          }}>
            <span>CA:</span>
            <code style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
              {MINT_ADDRESS.slice(0, 8)}...{MINT_ADDRESS.slice(-8)}
            </code>
            <button 
              onClick={handleCopyMint}
              style={{
                background: 'none',
                border: 'none',
                color: copied ? 'var(--accent-secondary)' : 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                padding: '0 0.25rem',
                transition: 'var(--transition)'
              }}
            >
              {copied ? 'Copied! ✅' : '📋'}
            </button>
          </div>
        </motion.div>
      </section>

      {/* 2. STATS BAR */}
      <section className="glass-card" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center', padding: '1.75rem 1rem' }}>
        <div>
          <h2 className="gradient-text" style={{ fontSize: '2.25rem' }}>100+</h2>
          <p className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Creators Registered</p>
        </div>
        <div style={{ borderLeft: '1px solid var(--border-glass)', borderRight: '1px solid var(--border-glass)' }}>
          <h2 className="gradient-text" style={{ fontSize: '2.25rem' }}>450K+</h2>
          <p className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>$SOLEM8 Tipped</p>
        </div>
        <div>
          <h2 className="gradient-text" style={{ fontSize: '2.25rem' }}>1,800+</h2>
          <p className="text-muted" style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Aesthetics Holders</p>
        </div>
      </section>

      {/* 3. HOW IT WORKS */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem', fontFamily: 'var(--font-heading)' }}>How It Works 👣</h2>
          <p className="text-secondary">Gain tier access dynamically simply by keeping tokens in your wallet.</p>
        </div>

        <div className="explore-grid">
          <div className="glass-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.25rem', marginBottom: '0.75rem' }}>🔌</div>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1.15rem' }}>1. Connect Wallet</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Sign a secure cryptographic message with your Solana wallet to authenticate instantly. No cards, no logins.</p>
          </div>
          <div className="glass-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.25rem', marginBottom: '0.75rem' }}>💎</div>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1.15rem' }}>2. Keep $SOLEM8</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Hold the utility token in your wallet. The smart gate automatically checks your balance to unlock matching tiers.</p>
          </div>
          <div className="glass-card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '2.25rem', marginBottom: '0.75rem' }}>🔑</div>
            <h3 style={{ marginBottom: '0.5rem', fontSize: '1.15rem' }}>3. Unlock & Support</h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>View high-res foot art instantly and tip creators directly with on-chain transfers taking only a 2% fee.</p>
          </div>
        </div>
      </section>

      {/* 4. UTILITY TIERS DETAIL */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem', fontFamily: 'var(--font-heading)' }}>Membership Tiers 🏆</h2>
          <p className="text-secondary">Hold `$SOLEM8` tokens to automatically reach elite levels.</p>
        </div>
        
        <div className="explore-grid">
          <div className="glass-card" style={{ borderLeft: '4px solid var(--tier-bronze)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--tier-bronze)' }}>Bronze 🥉</h3>
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>&ge; 1,000 tokens</span>
            </div>
            <p style={{ fontSize: '0.85rem' }}>Unlock starter portfolios and follow creators to build your aesthetic feed.</p>
          </div>
          <div className="glass-card" style={{ borderLeft: '4px solid var(--tier-silver)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--tier-silver)' }}>Silver 🥈</h3>
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>&ge; 10,000 tokens</span>
            </div>
            <p style={{ fontSize: '0.85rem' }}>Access premium high-resolution photo galleries and receive daily updates.</p>
          </div>
          <div className="glass-card" style={{ borderLeft: '4px solid var(--tier-gold)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--tier-gold)' }}>Gold 🥇</h3>
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>&ge; 100,000 tokens</span>
            </div>
            <p style={{ fontSize: '0.85rem' }}>Unlock VIP request privileges, behind-the-scenes video shoots, and close-ups.</p>
          </div>
          <div className="glass-card" style={{ borderLeft: '4px solid var(--tier-diamond)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ color: 'var(--tier-diamond)' }}>Diamond 💎</h3>
              <span style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>&ge; 1,000,000 tokens</span>
            </div>
            <p style={{ fontSize: '0.85rem' }}>Acquire elite master-class galleries, 1-on-1 private creator chats, and custom commissions.</p>
          </div>
        </div>
      </section>

      {/* 5. TOP CREATORS */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '0.5rem', fontFamily: 'var(--font-heading)' }}>Featured Sole Creators ⭐</h2>
          <p className="text-secondary">Explore portfolios from our most popular aesthetics models.</p>
        </div>

        <div className="explore-grid">
          {loading ? (
            Array(3).fill(0).map((_, idx) => (
              <div key={idx} className="glass-card shimmer" style={{ height: '180px' }} />
            ))
          ) : creators.length > 0 ? (
            creators.map((c) => (
              <motion.div 
                key={c.id} 
                className="glass-card" 
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/creator/${c.id}`)}
                whileHover={{ y: -5 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    borderRadius: '50%', 
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800
                  }}>
                    {c.display_name?.slice(0, 2).toUpperCase() || 'CR'}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.1rem' }}>{c.display_name || c.username || 'Creator'}</h3>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.follower_count} Followers</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.85rem', lineClamp: 2, overflow: 'hidden' }}>{c.bio || 'This creator hasn\'t updated their bio yet.'}</p>
              </motion.div>
            ))
          ) : (
            mockCreators.map((c, idx) => (
              <motion.div 
                key={idx} 
                className="glass-card" 
                style={{ cursor: 'pointer' }}
                onClick={() => navigate('/explore')}
                whileHover={{ y: -5 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ 
                    width: '50px', 
                    height: '50px', 
                    borderRadius: '50%', 
                    background: 'var(--gradient-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem'
                  }}>
                    {c.avatar}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.1rem' }}>{c.name}</h3>
                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.followers} Followers</p>
                  </div>
                </div>
                <p style={{ fontSize: '0.85rem' }}>{c.bio}</p>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* 6. BUY TOKEN CALL-TO-ACTION */}
      <section className="glass-card" style={{ 
        background: 'linear-gradient(135deg, rgba(255, 45, 120, 0.08), rgba(139, 92, 246, 0.08))',
        border: '1px solid rgba(255, 45, 120, 0.2)',
        textAlign: 'center',
        padding: '4rem 2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1.5rem',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{ 
          position: 'absolute', 
          top: '-20%', left: '-10%', 
          width: '300px', height: '300px', 
          background: 'radial-gradient(circle, rgba(255, 45, 120, 0.15), transparent 60%)', 
          zIndex: -1 
        }} />

        <h2 style={{ fontSize: '2.25rem', fontFamily: 'var(--font-heading)' }}>Get $SOLEM8 Tokens 💎</h2>
        <p style={{ maxWidth: '650px', fontSize: '1rem', lineHeight: 1.6 }}>
          Hold $SOLEM8 to auto-unlock exclusive foot aesthetics & high-res galleries from top creators. Direct on-chain tipping, zero credit card fees—pure degen utility for sole collectors. 🦶💎
        </p>

        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '0.5rem', 
          background: 'rgba(0,0,0,0.2)', 
          border: '1px solid var(--border-glass)',
          padding: '0.6rem 1.2rem', 
          borderRadius: '9999px',
          fontSize: '0.85rem',
          color: 'var(--text-secondary)',
          margin: '0.25rem 0'
        }}>
          <span>Mint:</span>
          <code style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>{MINT_ADDRESS}</code>
          <button 
            onClick={handleCopyMint}
            style={{
              background: 'none',
              border: 'none',
              color: copied ? 'var(--accent-secondary)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: '1rem',
              marginLeft: '0.25rem'
            }}
          >
            {copied ? 'Copied! ✅' : '📋'}
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', justifyContent: 'center', marginTop: '0.5rem' }}>
          <a href="https://pump.fun" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
            Buy on Pump.fun 💊
          </a>
          <a href="https://jup.ag" target="_blank" rel="noopener noreferrer" className="btn btn-secondary">
            Trade on Jupiter 🪐
          </a>
        </div>
      </section>

    </div>
  );
};
